import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  FORMA_PAGO,
  METODO_PAGO,
  entradasOrdenadas,
  formatearMoneda,
  type EstadoCotizacion,
  type FormaPago,
  type MetodoPago,
} from "@uirtus/shared";
import { useState } from "react";
import { FormularioCotizacion } from "../componentes/formulario-cotizacion";
import { ResumenTotales } from "../componentes/resumen-totales";
import {
  AreaTexto,
  Aviso,
  Boton,
  Campo,
  Confirmacion,
  Etiqueta,
  Modal,
  Seleccion,
  Tarjeta,
} from "../componentes/ui";
import { ErrorApi } from "../lib/api";
import {
  obtenerPdfCotizacion,
  useActualizarCotizacion,
  useCambiarEstadoCotizacion,
  useCotizacion,
  useEliminarCotizacion,
  useFacturarCotizacion,
  useNuevaVersionCotizacion,
} from "../lib/cotizaciones";

const ETIQUETA_ESTADO: Record<EstadoCotizacion, string> = {
  BORRADOR: "Borrador",
  ENVIADA: "Enviada",
  ACEPTADA: "Aceptada",
  RECHAZADA: "Rechazada",
  VENCIDA: "Vencida",
  CANCELADA: "Cancelada",
};

const TONO_ESTADO: Record<EstadoCotizacion, "neutro" | "exito" | "alerta" | "peligro"> = {
  BORRADOR: "neutro",
  ENVIADA: "alerta",
  ACEPTADA: "exito",
  RECHAZADA: "peligro",
  VENCIDA: "peligro",
  CANCELADA: "neutro",
};

/**
 * Nota recomendada, no obligatoria, en toda resolución — incluida la
 * aceptación: no siempre pasa por el link que ve el cliente. Cuando
 * autoriza por WhatsApp, llamada o correo, esta nota es el único rastro de
 * cómo se confirmó.
 */
const PIDE_MOTIVO: EstadoCotizacion[] = ["ACEPTADA", "RECHAZADA", "CANCELADA"];

const TITULO_TRANSICION: Partial<Record<EstadoCotizacion, string>> = {
  ENVIADA: "Enviar cotización",
  ACEPTADA: "Marcar como aceptada",
  RECHAZADA: "Marcar como rechazada",
  CANCELADA: "Cancelar cotización",
};

const ETIQUETA_MOTIVO: Partial<Record<EstadoCotizacion, string>> = {
  ACEPTADA: "¿Cómo confirmó el cliente?",
  RECHAZADA: "Motivo",
  CANCELADA: "Motivo",
};

const AYUDA_MOTIVO: Partial<Record<EstadoCotizacion, string>> = {
  ACEPTADA: 'Opcional: p. ej. "Confirmó por WhatsApp el 10/08". Sirve si después hay dudas.',
  RECHAZADA: "Opcional, pero ayuda a recordar por qué después",
  CANCELADA: "Opcional, pero ayuda a recordar por qué después",
};

const formatearFecha = (iso: string) =>
  new Intl.DateTimeFormat("es-MX", { day: "2-digit", month: "short", year: "numeric" }).format(
    new Date(`${iso}T00:00:00`),
  );

export function CotizacionDetalle() {
  const { id } = useParams({ from: "/cotizaciones/$id" });
  // TanStack Router reutiliza la instancia del componente cuando solo cambia
  // el parámetro de la ruta: navegar de una versión a otra no la remonta.
  // Sin la key, el aviso o el modal de la cotización anterior sobreviven al
  // cambio de id. La key fuerza que React trate cada id como un árbol nuevo.
  return <FichaCotizacion key={id} id={id} />;
}

function FichaCotizacion({ id }: { id: string }) {
  const navegar = useNavigate();
  const { data: cot, isLoading, isError, error } = useCotizacion(id);

  const [editando, setEditando] = useState(false);
  const [confirmarBaja, setConfirmarBaja] = useState(false);
  const [transicion, setTransicion] = useState<EstadoCotizacion | null>(null);
  const [motivo, setMotivo] = useState("");
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [facturaCreada, setFacturaCreada] = useState<{ id: string } | null>(null);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const [mostrarFacturar, setMostrarFacturar] = useState(false);
  const [metodoPagoFactura, setMetodoPagoFactura] = useState<MetodoPago>("PUE");
  const [formaPagoFactura, setFormaPagoFactura] = useState("03");

  const actualizar = useActualizarCotizacion(id);
  const cambiarEstado = useCambiarEstadoCotizacion(id);
  const nuevaVersion = useNuevaVersionCotizacion(id);
  const facturar = useFacturarCotizacion(id);
  const eliminar = useEliminarCotizacion(id);

  if (isLoading) return <p className="text-sm text-slate-500">Cargando…</p>;

  if (isError || !cot) {
    return (
      <div className="space-y-4">
        <Aviso>
          {error instanceof ErrorApi ? error.message : "No se pudo cargar la cotización"}
        </Aviso>
        <Link to="/cotizaciones" className="text-sm text-marca-600 hover:underline">
          ← Volver a cotizaciones
        </Link>
      </div>
    );
  }

  const cerrarTransicion = () => {
    setTransicion(null);
    setMotivo("");
  };

  const descargarPdf = async () => {
    setGenerandoPdf(true);
    try {
      const blob = await obtenerPdfCotizacion(id);
      const url = URL.createObjectURL(blob);
      // Descarga forzada con un <a download>, no window.open: un popup lo
      // bloquea cualquier navegador con esa opción activada (algo común en
      // un panel de administración), y así ni depende del permiso.
      const enlace = document.createElement("a");
      enlace.href = url;
      enlace.download = `${cot.folio}-v${cot.version}.pdf`;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setMensaje(e instanceof ErrorApi ? e.message : "No se pudo generar el PDF");
    } finally {
      setGenerandoPdf(false);
    }
  };

  return (
    <div className="space-y-5">
      <Link to="/cotizaciones" className="text-sm text-marca-600 hover:underline">
        ← Cotizaciones
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-mono text-lg font-semibold">
              {cot.folio}
              {cot.version > 1 && <span className="text-slate-400"> · v{cot.version}</span>}
            </h1>
            <Etiqueta tono={TONO_ESTADO[cot.estado]}>{ETIQUETA_ESTADO[cot.estado]}</Etiqueta>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">{cot.titulo}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Boton variante="secundario" disabled={generandoPdf} onClick={descargarPdf}>
            {generandoPdf ? "Generando…" : "Descargar PDF"}
          </Boton>

          {cot.estado === "BORRADOR" && (
            <>
              <Boton variante="secundario" onClick={() => setEditando(true)}>
                Editar
              </Boton>
              <Boton onClick={() => setTransicion("ENVIADA")}>Enviar</Boton>
              <Boton variante="peligro" onClick={() => setConfirmarBaja(true)}>
                Eliminar
              </Boton>
            </>
          )}

          {cot.estado === "ENVIADA" && (
            <>
              <Boton onClick={() => setTransicion("ACEPTADA")}>Aceptar</Boton>
              <Boton variante="secundario" onClick={() => setTransicion("RECHAZADA")}>
                Rechazar
              </Boton>
              <Boton variante="peligro" onClick={() => setTransicion("CANCELADA")}>
                Cancelar
              </Boton>
            </>
          )}

          {cot.estado === "VENCIDA" && (
            <Boton onClick={() => setTransicion("ENVIADA")}>Reenviar</Boton>
          )}

          {cot.estado === "ACEPTADA" && !facturaCreada && (
            <Boton
              onClick={() => {
                setMetodoPagoFactura("PUE");
                setFormaPagoFactura(cot.cliente.formaPagoDefault ?? "03");
                setMostrarFacturar(true);
              }}
            >
              Generar factura
            </Boton>
          )}

          <Boton
            variante="secundario"
            disabled={nuevaVersion.isPending}
            onClick={() =>
              nuevaVersion.mutate(undefined, {
                onSuccess: (v) =>
                  void navegar({ to: "/cotizaciones/$id", params: { id: v.id } }),
                onError: (e) => setMensaje(e.message),
              })
            }
          >
            Nueva versión
          </Boton>
        </div>
      </header>

      {mensaje && <Aviso tono="alerta">{mensaje}</Aviso>}

      {facturaCreada && (
        <Aviso tono="exito">
          Factura creada en borrador.{" "}
          <Link to="/facturas" className="underline">
            Ir a Facturas
          </Link>{" "}
          para timbrarla.
        </Aviso>
      )}

      {cot.versiones.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-500">Versiones:</span>
          {cot.versiones.map((v) => (
            <Link
              key={v.id}
              to="/cotizaciones/$id"
              params={{ id: v.id }}
              className={`rounded-full px-2 py-0.5 text-xs font-medium transition ${
                v.id === cot.id
                  ? "bg-marca-100 text-marca-700 dark:bg-marca-500/20 dark:text-marca-500"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              }`}
            >
              v{v.version} · {ETIQUETA_ESTADO[v.estado]}
            </Link>
          ))}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <Tarjeta titulo="Cliente">
          <div className="space-y-1 text-sm">
            <p className="font-medium">{cot.cliente.razonSocial}</p>
            <p className="font-mono text-xs text-slate-500">{cot.cliente.rfc}</p>
            {cot.cliente.email && (
              <p className="text-xs text-slate-500">{cot.cliente.email}</p>
            )}
            {cot.proyecto && (
              <p className="border-t border-slate-200 pt-1.5 text-xs dark:border-slate-800">
                <span className="text-slate-500">Proyecto: </span>
                <Link
                  to="/proyectos/$id"
                  params={{ id: cot.proyecto.id }}
                  className="text-marca-600 hover:underline dark:text-marca-500"
                >
                  {cot.proyecto.nombre}
                </Link>
              </p>
            )}
          </div>
        </Tarjeta>

        <Tarjeta titulo="Vigencia">
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">Válida hasta</dt>
              <dd>{formatearFecha(cot.validaHasta)}</dd>
            </div>
            {cot.enviadaAt && (
              <div className="flex justify-between">
                <dt className="text-slate-500">Enviada</dt>
                <dd>{new Date(cot.enviadaAt).toLocaleDateString("es-MX")}</dd>
              </div>
            )}
            {cot.resueltaAt && (
              <div className="flex justify-between">
                <dt className="text-slate-500">Resuelta</dt>
                <dd>{new Date(cot.resueltaAt).toLocaleDateString("es-MX")}</dd>
              </div>
            )}
          </dl>
          {cot.motivoResolucion && (
            <p className="mt-2 border-t border-slate-200 pt-2 text-xs text-slate-500 dark:border-slate-800">
              {cot.motivoResolucion}
            </p>
          )}
        </Tarjeta>

        <Tarjeta titulo="Condiciones">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {cot.condicionesDePago || "Sin condiciones especiales."}
          </p>
        </Tarjeta>
      </div>

      <Tarjeta titulo="Conceptos">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800">
              <tr>
                <th className="py-2 pr-4">Descripción</th>
                <th className="py-2 pr-4 text-right">Cantidad</th>
                <th className="py-2 pr-4 text-right">Valor unitario</th>
                <th className="py-2 text-right">Importe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {cot.conceptos.map((c) => (
                <tr key={c.id}>
                  <td className="py-2 pr-4">
                    <div>{c.descripcion}</div>
                    <div className="font-mono text-xs text-slate-400">
                      {c.claveProdServ} · {c.claveUnidad}
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-right font-mono">{c.cantidad}</td>
                  <td className="py-2 pr-4 text-right font-mono">
                    {formatearMoneda(c.valorUnitario, cot.moneda)}
                  </td>
                  <td className="py-2 text-right font-mono">
                    {formatearMoneda(c.importe, cot.moneda)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 border-t border-slate-200 pt-3 dark:border-slate-800">
          <ResumenTotales
            subtotal={cot.subtotal}
            descuento={cot.descuento}
            iva={cot.impuestosTrasladados}
            retenciones={cot.impuestosRetenidos}
            total={cot.total}
            moneda={cot.moneda}
          />
        </div>
      </Tarjeta>

      {cot.notas && (
        <Tarjeta titulo="Notas">
          <p className="whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">
            {cot.notas}
          </p>
        </Tarjeta>
      )}

      {/* --- Modales --- */}

      <Modal
        titulo="Editar cotización"
        abierto={editando}
        alCerrar={() => {
          setEditando(false);
          actualizar.reset();
        }}
        ancho="max-w-3xl"
      >
        <FormularioCotizacion
          cotizacion={cot}
          enviando={actualizar.isPending}
          errorServidor={actualizar.error ? actualizar.error.message : null}
          alEnviar={(datos) =>
            actualizar.mutate(datos as never, {
              onSuccess: () => {
                setEditando(false);
                actualizar.reset();
              },
            })
          }
          alCancelar={() => {
            setEditando(false);
            actualizar.reset();
          }}
        />
      </Modal>

      <Modal
        titulo={transicion ? (TITULO_TRANSICION[transicion] ?? "Cambiar estado") : ""}
        abierto={!!transicion}
        alCerrar={cerrarTransicion}
        ancho="max-w-md"
      >
        <div className="space-y-4 p-5">
          {transicion && PIDE_MOTIVO.includes(transicion) && (
            <Campo
              etiqueta={ETIQUETA_MOTIVO[transicion] ?? "Motivo"}
              ayuda={AYUDA_MOTIVO[transicion]}
            >
              <AreaTexto rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
            </Campo>
          )}
          <div className="flex justify-end gap-2">
            <Boton variante="secundario" onClick={cerrarTransicion}>
              Cancelar
            </Boton>
            <Boton
              variante={transicion === "CANCELADA" ? "peligro" : "primario"}
              disabled={cambiarEstado.isPending}
              onClick={() =>
                transicion &&
                cambiarEstado.mutate(
                  { estado: transicion, motivo: motivo.trim() || undefined },
                  { onSuccess: cerrarTransicion, onError: (e) => setMensaje(e.message) },
                )
              }
            >
              {cambiarEstado.isPending ? "Un momento…" : "Confirmar"}
            </Boton>
          </div>
        </div>
      </Modal>

      <Modal
        titulo="Generar factura"
        abierto={mostrarFacturar}
        alCerrar={() => setMostrarFacturar(false)}
        ancho="max-w-md"
      >
        <div className="space-y-4 p-5">
          <Campo etiqueta="Método de pago" requerido>
            <Seleccion
              value={metodoPagoFactura}
              onChange={(e) => setMetodoPagoFactura(e.target.value as MetodoPago)}
            >
              {entradasOrdenadas(METODO_PAGO).map(([clave, nombre]) => (
                <option key={clave} value={clave}>
                  {clave} · {nombre}
                </option>
              ))}
            </Seleccion>
          </Campo>

          {metodoPagoFactura === "PPD" ? (
            <p className="text-xs text-slate-500">
              Con pago en parcialidades la forma de pago queda en{" "}
              <strong>99 · Por definir</strong> — se reporta después, factura
              por factura, con el Complemento de Pagos.
            </p>
          ) : (
            <Campo etiqueta="Forma de pago" requerido>
              <Seleccion
                value={formaPagoFactura}
                onChange={(e) => setFormaPagoFactura(e.target.value)}
              >
                {entradasOrdenadas(FORMA_PAGO)
                  .filter(([clave]) => clave !== "99")
                  .map(([clave, nombre]) => (
                    <option key={clave} value={clave}>
                      {clave} · {nombre}
                    </option>
                  ))}
              </Seleccion>
            </Campo>
          )}

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
            <Boton variante="secundario" onClick={() => setMostrarFacturar(false)}>
              Cancelar
            </Boton>
            <Boton
              disabled={facturar.isPending}
              onClick={() =>
                facturar.mutate(
                  {
                    metodoPago: metodoPagoFactura,
                    formaPago:
                      metodoPagoFactura === "PPD" ? "99" : (formaPagoFactura as FormaPago),
                  },
                  {
                    onSuccess: (f) => {
                      setMostrarFacturar(false);
                      setFacturaCreada(f);
                    },
                    onError: (e) => setMensaje(e.message),
                  },
                )
              }
            >
              {facturar.isPending ? "Generando…" : "Generar factura"}
            </Boton>
          </div>
        </div>
      </Modal>

      <Confirmacion
        abierto={confirmarBaja}
        titulo="Eliminar cotización"
        mensaje={
          <>
            Se va a borrar <strong>{cot.folio}</strong> de forma definitiva. Solo es posible
            porque sigue en borrador.
          </>
        }
        textoConfirmar="Eliminar"
        peligroso
        ocupado={eliminar.isPending}
        alCancelar={() => setConfirmarBaja(false)}
        alConfirmar={() =>
          eliminar.mutate(undefined, {
            onSuccess: () => void navegar({ to: "/cotizaciones" }),
            onError: (e) => {
              setConfirmarBaja(false);
              setMensaje(e.message);
            },
          })
        }
      />
    </div>
  );
}
